import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, DollarSign, AlertTriangle, Navigation, Globe2 } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

export function ClassCard({ row, onClick }) {
    const isLive = row.live_source === "google_places";
    return (
        <article
            onClick={onClick}
            className="group bg-background p-6 md:p-7 cursor-pointer hover:bg-secondary transition-colors duration-200 relative flex flex-col justify-between min-h-[280px]"
            data-testid={`class-card-${row.id}`}
        >
            {/* Top-right corner labels */}
            <div className="absolute top-0 right-0 flex flex-col items-end">
                {isLive && (
                    <div className="bg-foreground text-background px-2 py-1 flex items-center gap-1 border-l border-b border-foreground">
                        <Globe2 className="h-3 w-3" />
                        <span className="font-mono-label text-[10px]">Live · Google</span>
                    </div>
                )}
                {row.manual_review && !isLive && (
                    <div className="bg-[#FFCC00] text-foreground px-2 py-1 flex items-center gap-1 border-l border-b border-foreground">
                        <AlertTriangle className="h-3 w-3" />
                        <span className="font-mono-label text-[10px]">Verify</span>
                    </div>
                )}
            </div>

            <div>
                <div className="flex items-center gap-2 mb-4">
                    <span className="font-mono-label text-[10px] text-muted-foreground">
                        {row.category}
                    </span>
                    <span className="text-muted-foreground" aria-hidden>
                        /
                    </span>
                    <span className="font-mono-label text-[10px] text-muted-foreground">
                        {row.borough || row.neighborhood || "NYC"}
                    </span>
                    {row.distance_miles != null && (
                        <>
                            <span className="text-muted-foreground" aria-hidden>·</span>
                            <span
                                className="font-mono-label text-[10px] text-primary flex items-center gap-1"
                                data-testid="card-distance"
                            >
                                <Navigation className="h-3 w-3" />
                                {row.distance_miles} mi
                            </span>
                        </>
                    )}
                </div>
                <h3 className="font-display text-2xl leading-tight mb-1 group-hover:text-primary transition-colors">
                    {row.class_name}
                </h3>
                <p className="font-editorial text-base text-muted-foreground mb-3">
                    at {row.studio_name}
                </p>

                {row.class_status && (
                    <div className="mb-4">
                        <StatusBadge status={row.class_status} />
                    </div>
                )}

                <p className="text-sm leading-relaxed text-foreground/80 line-clamp-3">
                    {row.description}
                </p>
            </div>

            <div className="mt-6 pt-4 border-t border-border flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" />
                    {row.neighborhood || row.address?.split(",")[0] || "—"}
                </span>
                {row.duration_minutes && (
                    <span className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        {row.duration_minutes} min
                    </span>
                )}
                {row.price_per_class && (
                    <span className="flex items-center gap-1.5">
                        <DollarSign className="h-3 w-3" />
                        {String(row.price_per_class).replace("$", "")}
                    </span>
                )}
                <Badge
                    variant="outline"
                    className="rounded-none border-foreground text-[10px] font-mono-label ml-auto"
                >
                    {(row.women_focus || "—").split(" / ")[0]}
                </Badge>
            </div>
        </article>
    );
}
